import React from 'react';
import getConfig from 'next/config';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
} from '@material-ui/core';

import { AddressAutocomplete } from '@/core/components/AddressAutocomplete';
import {
  AutoCompleteAddresses,
  CustomField,
  MessageForm,
} from '@/core/interfaces/common';

import {
  composeValidators,
  isEmail,
  phoneNumberValidator,
  required,
} from '@/utils/validators';
import { handleNumberFormat } from '@/utils/helpers';
import { fieldTypes } from '@/utils/constants';

import { StyledTextField } from '@/themes/globalStyles';

const { REACT_APP_SMARTY_STREETS_API_KEY } = getConfig().publicRuntimeConfig;

interface PrefixFieldProps {
  className?: string;
  prefixes: Array<string>;
}

interface StreetAddressFieldProps {
  addressSuggestions: AutoCompleteAddresses;
  change: <F extends keyof MessageForm>(name: F, value?: MessageForm[F] | undefined) => void;
  handleFetchAddressSuggestions: (inputValue: string, params: { selected?: string }) => void;
  isAutocompletePending: boolean;
  isKnownContact?: boolean;
}

interface CustomFieldProps {
  customFieldData: CustomField;
}

interface CustomFieldsProps {
  customFields: Array<CustomField>;
  render: (a: JSX.Element) => any;
}

interface MessageFieldProps {
  index: number;
}

export const PrefixField = ({
  className,
  prefixes,
}: PrefixFieldProps) => (
  <Field
    name="prefix"
    validate={required}
  >
    {({ input }) => (
      <StyledTextField
        fullWidth
        select
        className={className}
        label="Prefix"
        inputProps={{
          'aria-label': 'Prefix',
        }}
        SelectProps={{
          onChange: input.onChange,
          value: input.value,
        }}
      >
        {prefixes?.map(prefixOption => (
          <MenuItem
            key={prefixOption}
            value={prefixOption}
          >
            {prefixOption}
          </MenuItem>
        ))}
      </StyledTextField>
    )}
  </Field>
);

PrefixField.defaultProps = {
  className: '',
};

export const FirstNameField = () => (
  <Field
    name="firstName"
    validate={required}
  >
    {({ input }) => (
      <StyledTextField
        {...input}
        label="First Name"
        type="text"
      />
    )}
  </Field>
);

export const LastNameField = () => (
  <Field
    name="lastName"
    validate={required}
  >
    {({ input }) => (
      <StyledTextField
        {...input}
        label="Last Name"
        type="text"
      />
    )}
  </Field>
);

export const PhoneNumberField = () => (
  <Field
    name="phoneNumber"
    validate={composeValidators(required, phoneNumberValidator)}
  >
    {({
      input,
      meta,
    }) => (
      <StyledTextField
        {...input}
        fullWidth
        inputProps={{
          maxLength: 14,
        }}
        label="Phone number"
        onChange={event => {
          const { value } = event.target;

          input.onChange(handleNumberFormat(value));
        }}
        placeholder="(123) 123-1231"
        value={input.value || ''}
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
      />
    )}
  </Field>
);

export const EmailField = () => (
  <Field
    name="email"
    validate={composeValidators(required, isEmail)}
  >
    {({
      input,
      meta,
    }) => (
      <StyledTextField
        {...input}
        fullWidth
        label="Email"
        type="email"
        error={meta.touched && !!meta.error}
        helperText={meta.touched && meta.error}
      />
    )}
  </Field>
);

export const StreetAddressField = ({
  addressSuggestions,
  change,
  handleFetchAddressSuggestions,
  isAutocompletePending,
  isKnownContact,
}: StreetAddressFieldProps) => (
  <Field
    name="streetAddress"
    validate={required}
  >
    {({ input }) => (
      (
        isKnownContact ||
        !REACT_APP_SMARTY_STREETS_API_KEY
      ) ?
        (
          <StyledTextField
            {...input}
            autoComplete="new-value"
            fullWidth
            label="Street Address"
            type="text"
          />
        ) :
        (
          <AddressAutocomplete
            {...input}
            autoComplete="new-value"
            addressSuggestions={addressSuggestions}
            fetchAddressSuggestions={(inputValue, params) => (
              handleFetchAddressSuggestions(inputValue, params)
            )}
            isLoading={isAutocompletePending}
            setZipCode={newValue => change('zipCode', newValue)}
          />
        )
    )}
  </Field>
);

StreetAddressField.defaultProps = {
  isKnownContact: false,
};

export const ZipCodeField = () => (
  <Field
    name="zipCode"
    validate={required}
  >
    {({ input }) => (
      <StyledTextField
        {...input}
        autoComplete="new-value"
        fullWidth
        id="zipcode"
        label="Zip Code"
        type="text"
        inputProps={{
          maxLength: 5,
        }}
      />
    )}
  </Field>
);

export const CustomFieldWrapper = ({
  customFieldData: {
    fieldType,
    options,
    label,
    uuid,
  },
}: CustomFieldProps) => (
  <>
    {
      fieldType === fieldTypes.singleChoiceField ?
        (
          <FormControlWrapper>
            <FormControl component="fieldset">
              <FormLabel component="legend">{label}</FormLabel>
              {
                options.map(option => (
                  <div key={option}>
                    <Field
                      component="input"
                      name={`customFieldAnswers[${uuid}]`}
                      type="radio"
                      value={option}
                    >
                      {({ input }) => (
                        <FormControlLabel
                          control={<Radio color="primary" />}
                          {...input}
                          key={option}
                          label={option}
                          value={option}
                        />
                      )}
                    </Field>

                  </div>
                ))
              }
            </FormControl>
          </FormControlWrapper>
        ) :
        (
          <Field name={`customFieldAnswers[${uuid}]`}>
            {({ input }) => (
              <StyledTextField
                {...input}
                label={label}
              />
            )}
          </Field>
        )
    }
  </>
);

const FormControlWrapper = styled.div`
  margin-top: 15px;
`;

export const CustomFields = ({
  customFields,
  render,
}: CustomFieldsProps) => (
  <FieldArray name="customFields">
    {() => (customFields?.length ?
      (
        customFields?.map(customFieldData => (
          <div key={customFieldData.uuid}>
            {
            render(
              <CustomFieldWrapper
                key={customFieldData.uuid}
                customFieldData={customFieldData}
              />
            )}
          </div>
        ))
      ) :
      null
    )}
  </FieldArray>
);

export const SubjectField = () => (
  <Field
    name="subject"
    validate={required}
  >
    {({
      input,
      meta,
    }) => (
      <StyledTextField
        {...input}
        fullWidth
        type="text"
        error={
          (meta.touched && !!meta.error) ||
          (!meta.modifiedSinceLastSubmit && !!meta.submitError)
        }
        helperText={
          (meta.touched && meta.error) ||
          (!meta.modifiedSinceLastSubmit && meta.submitError)
        }
      />
    )}
  </Field>
);

export const MessageField = ({ index }: MessageFieldProps) => (
  <Field name={`message[${index}]`}>
    {({ input }) => (
      <StyledTextField
        {...input}
        fullWidth
        id={`message[${index}]`}
        inputProps={{
          'aria-label': `message paragraph ${index + 1}`,
        }}
        key={index}
        label="Editable box of content"
        multiline
        type="text"
      />
    )}
  </Field>
);

export const PersonalizedMessageField = () => (
  <Field name="personalizedMessage">
    {({ input }) => (
      <StyledTextField
        {...input}
        fullWidth
        label="Your personalized message(optional)"
        multiline
        type="text"
      />
    )}
  </Field>
);

export const IsOptEmailField = () => (
  <Field
    name="isOptInEmail"
    type="checkbox"
  >
    {({ input }) => (
      <FormControlLabel
        control={(
          <Checkbox
            {...input}
            color="primary"
          />
        )}
        label="Opt In to email updates"
      />
    )}
  </Field>
);

export const IsOptTextField = () => (
  <Field
    name="isOptInText"
    type="checkbox"
  >
    {({ input }) => (
      <FormControlLabel
        control={(
          <Checkbox
            {...input}
            color="primary"
          />
        )}
        label="Opt In to text updates"
      />
    )}
  </Field>
);
